import { useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { extractAffiliateName, processAffiliateData } from "../utils/helpers";
import { safeNumber } from "../scripts/historical-script";
import { DEFAULT_COMMISSION_PERCENTAGE } from "../utils/constants";
import {
  isGatewayTimeout,
  isStatementTimeout,
  statementTimeoutError,
} from "../utils/uploadErrors";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useSupabaseData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importMessage, setImportMessage] = useState("");
  const [debugInfo, setDebugInfo] = useState(null);

  // PERFORMANCE OPTIMIZATION: Simple caching layer
  const cacheRef = useRef({});

  const getCachedData = useCallback((key) => {
    const cached = cacheRef.current[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, []);

  const setCachedData = useCallback((key, data) => {
    cacheRef.current[key] = {
      data,
      timestamp: Date.now(),
    };
  }, []);

  const clearCache = () => {
    cacheRef.current = {};
  };

  // Timeout classification lives in ../utils/uploadErrors. The old inline
  // version treated Postgres statement-timeout kills (rolled back, data LOST)
  // as benign gateway timeouts ("will continue server-side") — which hid five
  // weeks of dead uploads (2026-06-20 -> 2026-07-27). A statement timeout must
  // surface as a hard failure; only true gateway timeouts are benign.
  const failIfStatementTimeout = (err, location) => {
    if (isStatementTimeout(err)) {
      console.error(
        `[CSV Import] ${location} was KILLED by the database statement timeout and rolled back. Nothing was saved.`,
        err
      );
      // Tagged with code 57014 so a cascade through the sibling .catch can
      // never re-classify it as a benign gateway timeout.
      throw statementTimeoutError();
    }
  };

  // Clear notifications after 5 seconds
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
    setDebugInfo(null);
    setImportProgress(0);
    setImportMessage("");
  }, []);

  // Fallback pagination method that handles large datasets
  const fetchAllDataWithPagination = async (tableName = "ad_performance") => {
    const startTime = Date.now();

    // Get total count first
    const { count: totalRecords, error: countError } = await supabase
      .from(tableName)
      .select("*", { count: "exact", head: true });

    if (countError) throw countError;

    const limit = 1000;
    const numPages = Math.ceil(totalRecords / limit);

    // Create all pagination requests as promises
    const promises = [];
    for (let page = 0; page < numPages; page++) {
      const from = page * limit;
      const to = from + limit - 1;

      promises.push(
        supabase
          .from(tableName)
          .select("*")
          .range(from, to)
          .order("id", { ascending: true })
          .then(({ data, error }) => {
            if (error) {
              console.error(`❌ Page ${page + 1} failed:`, error);
              throw error;
            }

            return data;
          })
      );
    }

    // Execute in smaller parallel chunks (faster than sequential, safer than all-at-once)
    const chunkSize = 8; // Process 8 pages at a time for optimal speed
    const allData = [];

    for (let i = 0; i < promises.length; i += chunkSize) {
      const chunk = promises.slice(i, i + chunkSize);

      const chunkResults = await Promise.all(chunk);
      allData.push(...chunkResults.flat());
    }

    const endTime = Date.now();
    const loadTime = (endTime - startTime) / 1000;

    setSuccess(
      `Loaded ${allData.length} records from database in ${loadTime.toFixed(
        1
      )}s`
    );
    return allData;
  };

  // SUPER FAST optimized function to fetch ALL data with parallel loading
  const fetchAllDataOptimized = async () => {
    const startTime = Date.now();

    // First, get the total count quickly
    const { count, error: countError } = await supabase
      .from("ad_performance")
      .select("*", { count: "exact", head: true });

    if (countError) throw countError;

    const totalRecords = count || 0;

    if (totalRecords === 0) {
      setSuccess("No records found");
      return [];
    }

    // Use optimal batch size for production (1k per batch for reliability)
    const batchSize = 1000;
    const numBatches = Math.ceil(totalRecords / batchSize);

    // Create all promises for parallel execution
    const promises = [];
    for (let i = 0; i < numBatches; i++) {
      const from = i * batchSize;
      const to = Math.min(from + batchSize - 1, totalRecords - 1);

      promises.push(
        supabase
          .from("ad_performance")
          .select("*")
          .range(from, to)
          .order("id", { ascending: true })
          .then(({ data, error }) => {
            if (error) {
              console.error(`❌ Batch ${i + 1} failed:`, error);
              throw error;
            }

            return data;
          })
          .catch((batchError) => {
            console.error(`💥 Batch ${i + 1} error:`, batchError);
            throw batchError;
          })
      );
    }

    // Execute promises in smaller parallel chunks to avoid overwhelming the API
    const chunkSize = 5; // Process 5 batches at a time
    const allBatches = [];

    for (let i = 0; i < promises.length; i += chunkSize) {
      const chunk = promises.slice(i, i + chunkSize);

      const chunkResults = await Promise.all(chunk);

      allBatches.push(...chunkResults);
    }

    const batches = allBatches;

    // Combine all batches into final dataset
    const allData = batches.flat();

    const endTime = Date.now();
    const loadTime = (endTime - startTime) / 1000;

    setSuccess(
      `🚀 Loaded all ${allData.length} records in ${loadTime.toFixed(1)}s`
    );

    return allData;
  };

  // Load ALL data from Supabase (FAST parallel loading with fallback)
  // TODO is this needed?
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      try {
        // Try optimized parallel loading first
        const allData = await fetchAllDataOptimized();

        // Verify we got reasonable amount of data (check for actual missing data vs empty records)
        const { count: expectedCount } = await supabase
          .from("ad_performance")
          .select("*", { count: "exact", head: true });

        // Check if we have a reasonable amount of data (more flexible threshold)
        const dataPercentage = (allData.length / expectedCount) * 100;

        // If we got less than 30% of expected records, something is definitely wrong
        if (allData.length < expectedCount * 0.3) {
          console.warn(
            `⚠️ Parallel loading definitely incomplete: got only ${dataPercentage.toFixed(
              1
            )}% of records. Falling back to pagination...`
          );
          throw new Error("Incomplete parallel loading");
        }

        return allData;
      } catch (parallelError) {
        console.warn(
          "Parallel loading failed, falling back to pagination:",
          parallelError.message
        );

        // Fallback to pagination method
        const paginatedData = await fetchAllDataWithPagination();
        return paginatedData;
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);
  // Get available dates that have data (FIXED - gets ALL dates, not just first 1000)
  const getAvailableDates = useCallback(async () => {
    try {
      // Method 1: Try to use SQL aggregation to get unique dates efficiently
      try {
        const { data, error } = await supabase.rpc("get_unique_dates");

        if (!error && data) {
          return data.map((row) => row.date).sort();
        }
      } catch (rpcError) {
        console.log("RPC function not available, using fallback method");
      }

      // Method 2: Fallback - Get ALL records with pagination to ensure we don't miss any

      const allDates = new Set();
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("ad_performance")
          .select("date")
          .range(from, from + batchSize - 1)
          .order("date", { ascending: true });

        if (error) throw error;

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        // Add dates to Set (automatically handles uniqueness)
        data.forEach((row) => {
          if (row.date) {
            allDates.add(row.date);
          }
        });

        // If we got less than the batch size, we're done
        if (data.length < batchSize) {
          hasMore = false;
        } else {
          from += batchSize;
        }
      }

      const uniqueDatesArray = Array.from(allDates).sort();

      return uniqueDatesArray;
    } catch (err) {
      console.error("Error getting available dates:", err);
      return [];
    }
  }, []);

  // Get date range summary - OPTIMIZED
  const getDateRangeSummary = useCallback(async () => {
    try {
      // OPTIMIZATION: Use server-side aggregation instead of fetching all records
      const { data, error } = await supabase
        .from("ad_performance")
        .select("date, spend, revenue")
        .order("date", { ascending: true })
        .limit(10000); // Reasonable limit to prevent timeouts

      if (error) throw error;

      if (!data || data.length === 0) return null;

      // Group by date more efficiently
      const dateGroups = {};
      for (const row of data) {
        const date = row.date;
        if (!dateGroups[date]) {
          dateGroups[date] = { date, spend: 0, revenue: 0, count: 0 };
        }
        dateGroups[date].spend += parseFloat(row.spend) || 0;
        dateGroups[date].revenue += parseFloat(row.revenue) || 0;
        dateGroups[date].count += 1;
      }

      const summary = Object.values(dateGroups);

      return {
        dates: summary,
        earliestDate: summary[0]?.date,
        latestDate: summary[summary.length - 1]?.date,
        totalDaysWithData: summary.length,
      };
    } catch (err) {
      console.error("Error getting date range summary:", err);

      // FALLBACK: Try to get just min/max dates if full summary fails
      try {
        const { data: minMaxData, error: minMaxError } = await supabase
          .from("ad_performance")
          .select("date")
          .order("date", { ascending: true })
          .limit(1);

        const { data: maxData, error: maxError } = await supabase
          .from("ad_performance")
          .select("date")
          .order("date", { ascending: false })
          .limit(1);

        if (
          !minMaxError &&
          !maxError &&
          minMaxData?.length > 0 &&
          maxData?.length > 0
        ) {
          return {
            dates: [],
            earliestDate: minMaxData[0].date,
            latestDate: maxData[0].date,
            totalDaysWithData: 0,
          };
        }
      } catch (fallbackErr) {
        console.error("Fallback date range failed:", fallbackErr);
      }

      return null;
    }
  }, []);

  // NEW FUNCTIONS START HERE:====
  // Function to pull all affiliate data from ad_performance table (not summarized)
  const pullAllAffiliateData = useCallback(
    async (brand = "all", dateFilter = null) => {
      try {
        setIsLoading(true);
        setError(null);

        // First, get all users from profiles table
        const { data: allUsers, error: usersError } = await supabase
          .from("profiles")
          .select("id, username, email")
          .order("username");

        if (usersError) throw usersError;

        // OPTIMIZED: Use RPC for server-side aggregation
        // This returns ~20 rows instead of 10,000+, with pre-calculated commission
        const brandFilter = brand && brand !== "all" ? brand : null;
        let startDate = null;
        let endDate = null;

        if (dateFilter && dateFilter.type !== "all") {
          if (dateFilter.type === "single" && dateFilter.singleDate) {
            startDate = dateFilter.singleDate;
            endDate = dateFilter.singleDate;
          } else if (
            dateFilter.type === "range" &&
            dateFilter.dateRangeStart &&
            dateFilter.dateRangeEnd
          ) {
            startDate = dateFilter.dateRangeStart;
            endDate = dateFilter.dateRangeEnd;
          }
        }

        const { data: summaryData, error: rpcError } = await supabase.rpc(
          "get_admin_affiliate_summary",
          {
            p_brand_filter: brandFilter,
            p_start_date: startDate,
            p_end_date: endDate,
          }
        );

        if (rpcError) throw rpcError;

        // Initialize affiliate map with all users showing $0.00 earnings
        const affiliateMap = new Map();
        allUsers.forEach((user) => {
          const userName =
            user.username || user.email?.split("@")[0] || "Unknown";
          affiliateMap.set(userName, {
            name: userName,
            ads: [], // Empty - individual ads loaded separately if needed
            totalAdSpend: 0,
            totalRevenue: 0,
            totalProfit: 0,
            totalCommission: 0,
            totalPurchases: 0,
            adCount: 0,
            commissionBreakdown: {},
            brand: brandFilter || "",
            avgROAS: 0,
            avgCommissionRate: 0,
          });
        });

        // Update affiliate map with RPC results
        let processedRows = 0;
        (summaryData || []).forEach((row) => {
          const affiliateName = row.affiliate_name;
          if (!affiliateName || affiliateName === "Unknown") return;

          // Get or create affiliate entry
          let affiliate = affiliateMap.get(affiliateName);
          if (!affiliate) {
            affiliate = {
              name: affiliateName,
              ads: [],
              totalAdSpend: 0,
              totalRevenue: 0,
              totalProfit: 0,
              totalCommission: 0,
              totalPurchases: 0,
              adCount: 0,
              commissionBreakdown: {},
              brand: brandFilter || "",
              avgROAS: 0,
              avgCommissionRate: 0,
            };
            affiliateMap.set(affiliateName, affiliate);
          }

          // Map RPC response to expected structure
          affiliate.totalAdSpend = parseFloat(row.total_ad_spend) || 0;
          affiliate.totalRevenue = parseFloat(row.total_revenue) || 0;
          affiliate.totalProfit = parseFloat(row.total_profit) || 0;
          affiliate.totalCommission = parseFloat(row.total_commission) || 0;
          affiliate.totalPurchases = parseInt(row.total_purchases) || 0;
          affiliate.adCount = parseInt(row.ad_count) || 0;
          affiliate.avgROAS = parseFloat(row.avg_roas) || 0;
          affiliate.avgCommissionRate = parseFloat(row.avg_commission_rate) || 0;

          // Commission breakdown comes as JSONB from RPC
          affiliate.commissionBreakdown = row.commission_breakdown || {};

          processedRows += affiliate.adCount;
        });

        setSuccess(`Loaded summary for ${summaryData?.length || 0} affiliates via RPC`);
        return {
          affiliates: Array.from(affiliateMap.values()),
          processedRows,
          skippedRows: 0,
        };
      } catch (err) {
        console.error("Pull all affiliate data error:", err);
        setError(err.message);
        return { affiliates: [], processedRows: 0, skippedRows: 0 };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const pullSummarizedData = useCallback(
    async (affiliateName, brand, dateFilter = null) => {
      // First, get all users from profiles table
      const { data: allUsers, error: usersError } = await supabase
        .from("profiles")
        .select("id, username, email")
        .order("username");

      if (usersError) throw usersError;

      // OPTIMIZED: Use RPC for server-side aggregation
      // This returns ~20 rows instead of 10,000+, with pre-calculated commission
      const brandFilter = brand && brand !== "all" ? brand : null;
      let startDate = null;
      let endDate = null;

      if (dateFilter && dateFilter.type !== "all") {
        if (dateFilter.type === "single" && dateFilter.singleDate) {
          startDate = dateFilter.singleDate;
          endDate = dateFilter.singleDate;
        } else if (
          dateFilter.type === "range" &&
          dateFilter.dateRangeStart &&
          dateFilter.dateRangeEnd
        ) {
          startDate = dateFilter.dateRangeStart;
          endDate = dateFilter.dateRangeEnd;
        }
      }

      const { data: summaryData, error: rpcError } = await supabase.rpc(
        "get_admin_affiliate_summary",
        {
          p_brand_filter: brandFilter,
          p_start_date: startDate,
          p_end_date: endDate,
        }
      );

      if (rpcError) throw rpcError;

      // Initialize affiliate map with all users showing $0.00 earnings
      const affiliateMap = new Map();
      allUsers.forEach((user) => {
        const userName =
          user.username || user.email?.split("@")[0] || "Unknown";
        affiliateMap.set(userName, {
          name: userName,
          ads: [], // Empty - individual ads loaded separately if needed
          totalAdSpend: 0,
          totalRevenue: 0,
          totalProfit: 0,
          totalCommission: 0,
          totalPurchases: 0,
          adCount: 0,
          commissionBreakdown: {},
          brand: brandFilter || "",
          avgROAS: 0,
          avgCommissionRate: 0,
        });
      });

      // Filter by affiliate name if specified
      let filteredData = summaryData || [];
      if (affiliateName && affiliateName.length > 0) {
        filteredData = filteredData.filter(
          (row) => row.affiliate_name === affiliateName
        );
      }

      // Update affiliate map with RPC results
      let processedRows = 0;
      filteredData.forEach((row) => {
        const affName = row.affiliate_name;
        if (!affName || affName === "Unknown") return;

        // Get or create affiliate entry
        let affiliate = affiliateMap.get(affName);
        if (!affiliate) {
          affiliate = {
            name: affName,
            ads: [],
            totalAdSpend: 0,
            totalRevenue: 0,
            totalProfit: 0,
            totalCommission: 0,
            totalPurchases: 0,
            adCount: 0,
            commissionBreakdown: {},
            brand: brandFilter || "",
            avgROAS: 0,
            avgCommissionRate: 0,
          };
          affiliateMap.set(affName, affiliate);
        }

        // Map RPC response to expected structure
        affiliate.totalAdSpend = parseFloat(row.total_ad_spend) || 0;
        affiliate.totalRevenue = parseFloat(row.total_revenue) || 0;
        affiliate.totalProfit = parseFloat(row.total_profit) || 0;
        affiliate.totalCommission = parseFloat(row.total_commission) || 0;
        affiliate.totalPurchases = parseInt(row.total_purchases) || 0;
        affiliate.adCount = parseInt(row.ad_count) || 0;
        affiliate.avgROAS = parseFloat(row.avg_roas) || 0;
        affiliate.avgCommissionRate = parseFloat(row.avg_commission_rate) || 0;

        // Commission breakdown comes as JSONB from RPC
        affiliate.commissionBreakdown = row.commission_breakdown || {};

        processedRows += affiliate.adCount;
      });

      return {
        affiliates: Array.from(affiliateMap.values()),
        processedRows,
        skippedRows: 0,
      };
    },
    []
  );

  const importCSVDataInNewDashboard = useCallback(
    async (csvData, brand) => {
      // Default to a very old date so that when the DB is empty, we import everything
      let maxDateInDB = "1900-01-01";
      try {
        // Initialize progress tracking
        setImportProgress(0);
        setImportMessage("Initializing import...");

        // Get max date from the database for THIS brand only
        let maxDateQuery = supabase
          .from("ad_performance")
          .select("date")
          .order("date", { ascending: false })
          .limit(1);

        if (brand && brand !== "all" && brand !== "none") {
          maxDateQuery = maxDateQuery.eq("brand", brand);
        }

        const { data: maxDateData, error: maxDateError } = await maxDateQuery;
        if (maxDateError) throw maxDateError;

        // If we have an existing max date in the DB, use it; otherwise keep the old baseline
        if (maxDateData && maxDateData.length > 0 && maxDateData[0].date) {
          maxDateInDB = maxDateData[0].date;
        }

        setImportProgress(5);
        setImportMessage("Database connection established...");
      } catch (err) {
        console.error("Error getting max date:", err);
      }

      try {
        setIsLoading(true);
        setError(null);

        const { data: affiliateNameData, error: affiliateNameError } =
          await supabase.from("affiliate_entity").select("*");
        if (affiliateNameError) throw affiliateNameError;
        // Do not abort if no affiliates exist yet; we upsert affiliates later
        const safeAffiliateNameData = affiliateNameData || [];

        setImportProgress(10);
        setImportMessage("Processing CSV data...");

        // Use the new bulk insert function for better performance
        // Step 1: map CSV → rows
        const mappedData = csvData.map((row) => {
          const spend =
            parseFloat(row["Ad Spend"]?.toString().replace(/[$,]/g, "")) || 0;
          const revenue =
            parseFloat(row["Pixel CV"]?.toString().replace(/[$,]/g, "")) || 0;
          const profit = revenue - spend;
          const commission = spend * DEFAULT_COMMISSION_PERCENTAGE; // Default commission rate

          const campaignName = row["Campaign Name"] || "Unknown Campaign";
          const affiliateName = extractAffiliateName(row["Ad Name"]);

          const rawDateInput = row["Date"] ? row["Date"].toString().trim() : "";
          let normalizedTimestamp = null;
          let parsedDate = new Date().toISOString().split("T")[0];

          try {
            if (rawDateInput) {
              const hourMatch = rawDateInput.match(
                /^(\d{4}-\d{2}-\d{2})T(\d{1,2})(?::(\d{2})(?::(\d{2}))?)?$/
              );

              if (hourMatch) {
                const dayPart = hourMatch[1];
                const hourPart = hourMatch[2].padStart(2, "0");
                const minutePart = hourMatch[3] ?? "00";
                const secondPart = hourMatch[4] ?? "00";
                normalizedTimestamp = `${dayPart}T${hourPart}:${minutePart}:${secondPart}`;
                parsedDate = dayPart;
              } else {
                const parsed = new Date(rawDateInput);
                if (!isNaN(parsed.getTime())) {
                  parsedDate = parsed.toISOString().split("T")[0];
                  normalizedTimestamp = rawDateInput.includes("T")
                    ? rawDateInput
                    : parsed.toISOString();
                } else {
                  console.warn(
                    `Invalid date format: "${row["Date"]}", using current date`
                  );
                }
              }
            }
          } catch (dateError) {
            console.warn(
              `Date parsing error for "${row["Date"]}":`,
              dateError.message
            );
          }

          if (!normalizedTimestamp && rawDateInput) {
            normalizedTimestamp = rawDateInput;
            if (rawDateInput.includes("T")) {
              parsedDate = rawDateInput.split("T")[0];
            }
          }

          const affiliateUUID = safeAffiliateNameData.find(
            (a) => a.name.toLowerCase() === affiliateName.toLowerCase()
          )?.id;

          return {
            affiliate_name: affiliateName,
            campaign_name: campaignName,
            adset_name: row["Adset Name"] || "",
            ad_name: row["Ad Name"] || "Unknown Ad",
            date: parsedDate,
            captured_at: normalizedTimestamp,
            raw_timestamp: rawDateInput || null,
            spend: spend,
            revenue: revenue,
            profit: profit,
            commission: commission,
            clicks: parseInt(row["Pixel Sessions"]) || 0, // Using sessions as clicks proxy
            impressions: 0, // Not available in this CSV structure
            purchases: parseInt(row["Pixel Purchases"]) || 0,
            cpa: row["Pixel CPA"]
              ? parseFloat(row["Pixel CPA"]?.toString().replace(/[$,]/g, ""))
              : null,
            aov: row["Pixel AOV"]
              ? parseFloat(row["Pixel AOV"]?.toString().replace(/[$,]/g, ""))
              : null,
            affiliate_uuid: affiliateUUID,
            brand: brand,
          };
        });

        // Step 2: apply date filter to only import newer-than-DB rows
        let formattedData = mappedData.filter((row) => {
          const date = row.date;
          return new Date(date) > new Date(maxDateInDB);
        });

        // Fallback: if nothing passes the filter, import all mapped rows
        if (formattedData.length === 0) {
          console.warn(
            `⚠️ No rows newer than max date (${maxDateInDB}). Falling back to import all ${mappedData.length} rows for brand: ${brand}.`
          );
          formattedData = mappedData;
        }

        // For very large datasets (>10k records), use the streaming function
        // For medium datasets, use optimized function, for small datasets use original
        let functionName = "streaming_bulk_insert_ad_performance";

        // Use the appropriate bulk insert function
        const rpcParams = { data: formattedData };
        if (functionName === "streaming_bulk_insert_ad_performance") {
          rpcParams.batch_size = 2000;
        }

        setImportProgress(20);
        setImportMessage(
          `Formatted ${formattedData.length} records for import...`
        );

        // IMPORTANT: Insertion to the summary table logic happens here:
        // convert data back to supabase data
        const csvDataFromSupabase = formattedData.map((row) => ({
          Date: row.date || "",
          "Ad Name": row.ad_name || "",
          "Campaign Name": row.campaign_name || "",
          "Adset Name": row.adset_name || "",
          "Ad Spend": row.spend || 0,
          "Pixel CV": row.revenue || 0,
          ROAS: row.spend > 0 ? row.revenue / row.spend : 0, // Calculate ROAS
          "Pixel ROAS": row.spend > 0 ? row.revenue / row.spend : 0, // Same as ROAS
          "Pixel Purchases": row.purchases || 0,
          "Pixel CPA": row.cpa || "",
          "Pixel AOV": row.aov || "",
          Profit: row.profit || 0,
          Commission: row.commission || 0,
          Clicks: row.clicks || 0,
          Impressions: row.impressions || 0,
          // Add affiliate extraction logic
          affiliate: row.affiliate_name || "",
          brand: brand,
        }));

        // process the affiliate data
        const { affiliates } = processAffiliateData(csvDataFromSupabase);

        setImportProgress(30);
        setImportMessage(`Processing ${affiliates.length} affiliates...`);

        // 4.3 Upsert affiliates
        const affiliateEntities = affiliates.map((a) => ({
          name: a.name,
          // id will be auto-generated or handled by Supabase
        }));
        // Deduplicate by name
        const uniqueAffiliateNames = Array.from(
          new Set(affiliateEntities.map((a) => a.name))
        ).map((name) => ({ name }));

        // Upsert affiliate entities
        let { error: affiliateError, data: affiliateData } = await supabase
          .from("affiliate_entity")
          .upsert(uniqueAffiliateNames, { onConflict: ["name"] })
          .select("*");
        if (affiliateError) throw affiliateError;

        setImportProgress(40);
        setImportMessage("Creating affiliate entities...");

        let outRows = [];
        for (const item of affiliates) {
          const affiliateId = affiliateData.find(
            (a) => a.name === item.name
          )?.id;
          for (const ad of item.ads) {
            outRows.push({
              affiliate_id: affiliateId,
              affiliate_name: item.name,
              ad_name: ad.adName,
              ad_spend: safeNumber(ad.adSpend),
              revenue: safeNumber(ad.revenue),
              roas: safeNumber(ad.roas),
              profit: safeNumber(ad.profit),
              commission: safeNumber(ad.commission),
              purchases: safeNumber(ad.purchases),
              cpa: safeNumber(ad.cpa),
              aov: safeNumber(ad.aov),
              campaign_name: ad.campaignName,
              adset_name: ad.adsetName,
              commission_tier: ad.commissionTier ?? null,
              commission_label: ad.commissionLabel ?? null,
              brand,
            });
          }
        }

        // Main Logic for upserting the data to summary table here:
        setImportProgress(50);
        setImportMessage(
          `Running bulk inserts for ${outRows.length} records...`
        );

        // The client-side summary upsert is intentionally GONE. The server
        // pipeline (streaming_bulk_insert_ad_performance -> summary rebuilds)
        // derives affiliate_earnings_summary from ad_performance in the SAME
        // transaction, so uploading a client-aggregated copy was redundant —
        // and dangerous: when the two parallel RPCs' fates diverged (summary
        // committed, insert rolled back), the tables disagreed, which tripped
        // the pipeline's drift detector and forced full rebuilds/timeouts on
        // every subsequent upload (incident 2026-06-20 -> 2026-07-27).
        // Summaries are now server-derived only.
        const summaryPromise = Promise.resolve({
          skipped: true,
          insertedSummary: 0,
          updatedSummary: 0,
          durationMs: 0,
          processed: 0,
          timedOut: false,
        });

        const performanceBatchSize = Math.min(
          5000,
          Math.max(1000, formattedData.length)
        );
        const performanceStart = performance.now();
        const performancePromise = supabase
          .rpc(functionName, {
            data: formattedData,
            batch_size: performanceBatchSize,
          })
          .then(({ data, error }) => {
            if (error) {
              failIfStatementTimeout(error, "performance insert");
              if (isGatewayTimeout(error)) {
                console.warn(
                  "[CSV Import] performance insert timed out at the gateway but will continue server-side."
                );
                return {
                  timedOut: true,
                  location: "performance",
                  method: error?.method,
                };
              }
              throw error;
            }
            if (!data?.success) {
              throw new Error(
                data?.message || `Bulk insert failed: ${JSON.stringify(data)}`
              );
            }

            const durationMs = performance.now() - performanceStart;
            const insertedCount = data.inserted_count ?? formattedData.length;
            const method = data.method || "optimized chunks";

            console.info(
              `[CSV Import] performance insert (${method}) processed ${insertedCount} rows in ${durationMs.toFixed(0)}ms (batch_size=${performanceBatchSize})`
            );

            return {
              insertedCount,
              durationMs,
              method,
              timedOut: false,
            };
          })
          .catch((err) => {
            failIfStatementTimeout(err, "performance insert");
            if (isGatewayTimeout(err)) {
              console.warn(
                "[CSV Import] performance insert timed out at the gateway but will continue server-side."
              );
              return {
                timedOut: true,
                location: "performance",
              };
            }
            throw err;
          });

        setImportProgress(60);
        setImportMessage(
          "Waiting for summary and performance inserts to finish..."
        );

        const [summaryResult, performanceResult] = await Promise.all([
          summaryPromise,
          performancePromise,
        ]);

        const timedOutUpload =
          summaryResult?.timedOut || performanceResult?.timedOut;

        if (timedOutUpload) {
          // A gateway timeout is INDETERMINATE, not success: Postgres usually
          // keeps executing after the gateway hangs up and commits — but if it
          // later hits the 120s DB statement timeout it rolls back instead.
          // Verify against the durable audit row that the upload wrapper
          // commits atomically with the data (processing_jobs.job_type =
          // 'csv_upload', uploaded_by = this user). Confirmed row => the
          // performance insert really committed. No row => do NOT claim
          // success. (Lesson of the 2026-06/07 incident: never report an
          // upload as saved without server-side evidence.)
          setImportProgress(90);
          setImportMessage(
            "The request timed out at the gateway — verifying with the database whether the upload actually saved..."
          );

          const startedBefore = new Date(
            Date.now() - 5 * 60 * 1000
          ).toISOString();
          const {
            data: { user: currentUser },
          } = await supabase.auth.getUser();

          let confirmedJob = null;
          if (performanceResult?.timedOut && currentUser?.id) {
            // 20 x 15s = 5 min: measured pipeline time is ~40s on the fast
            // path but ~4 min when the drift fallback runs a full rebuild.
            for (let attempt = 0; attempt < 20 && !confirmedJob; attempt++) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => setTimeout(resolve, 15000));
              // eslint-disable-next-line no-await-in-loop
              const { data: jobs } = await supabase
                .from("processing_jobs")
                .select("id, created_at, total_rows")
                .eq("job_type", "csv_upload")
                .eq("uploaded_by", currentUser.id)
                // Row count disambiguates from an earlier file uploaded
                // within the same window.
                .eq("total_rows", formattedData.length)
                .gte("created_at", startedBefore)
                .order("created_at", { ascending: false })
                .limit(1);
              if (jobs && jobs.length > 0) {
                confirmedJob = jobs[0];
              }
            }
          } else if (!performanceResult?.timedOut) {
            // Only the (fast, already-committed) summary call timed out at the
            // gateway; the performance insert returned success directly.
            confirmedJob = { assumed: true };
          }

          setDebugInfo({
            summary: summaryResult,
            performance: {
              ...performanceResult,
              batchSize: performanceBatchSize,
            },
            confirmedJob,
          });

          if (confirmedJob) {
            setImportProgress(100);
            setImportMessage(
              "Upload confirmed saved (verified against the database job log)."
            );
            setSuccess(
              "Upload confirmed: the database recorded the import. Totals may take a moment to refresh."
            );
            return { success: true, pending: false, verified: true };
          }

          setImportProgress(95);
          setImportMessage(
            "Could not confirm the upload finished. Do not assume it saved."
          );
          setError(
            "UNCONFIRMED: the request timed out and the database has not recorded the import yet. " +
              "Wait a few minutes, refresh, and check whether the day's totals appear before assuming success. " +
              "Re-uploading the same file is safe — duplicate rows are skipped automatically."
          );
          return { success: false, pending: true, verified: false };
        }

        const {
          insertedSummary,
          updatedSummary,
          durationMs: summaryDuration,
          processed: summaryProcessed,
        } = summaryResult || {};
        const {
          insertedCount: insertedPerformance,
          durationMs: performanceDuration,
          method: performanceMethod,
        } = performanceResult || {};

        setImportProgress(70);
        setImportMessage(
          `Bulk inserts complete (summary: ${insertedSummary} inserted/${updatedSummary} updated in ${(summaryDuration / 1000).toFixed(1)}s, performance: ${insertedPerformance} rows in ${(performanceDuration / 1000).toFixed(1)}s)...`
        );

        const totalInserted = insertedPerformance;

        setDebugInfo({
          summary: {
            rows: summaryProcessed,
            inserted: insertedSummary,
            updated: updatedSummary,
            durationMs: summaryDuration,
          },
          performance: {
            rows: insertedPerformance,
            durationMs: performanceDuration,
            method: performanceMethod,
            batchSize: performanceBatchSize,
          },
        });

        setSuccess(
          `Imported ${totalInserted} rows (summary ${(summaryDuration / 1000).toFixed(1)}s, performance ${(performanceDuration / 1000).toFixed(1)}s, method: ${performanceMethod})`
        );


        setImportProgress(100);
        setImportMessage(
          `Import complete! ${totalInserted} records imported successfully.`
        );

        // Refresh the data
        return { success: true, inserted_count: totalInserted };
      } catch (err) {
        console.error("Import error:", err);
        setError(err.message);
        setImportProgress(0);
        setImportMessage("Import failed: " + err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const importAdPerformanceDataByDates = useCallback(
    async (type = "single", dates, affiliateName, brand) => {
      try {
        setIsLoading(true);
        setError(null);

        let totalCount = 0;
        let filter = null;

        const { data: affiliateNameData, error: affiliateNameError } =
          await supabase.from("affiliate_entity").select("id, name");
        if (affiliateNameError) throw affiliateNameError;

        let affiliateUUID = null;
        if (affiliateName && affiliateName.length > 0) {
          affiliateUUID =
            affiliateNameData.find(
              (a) => a.name.toLowerCase() === affiliateName.toLowerCase()
            )?.id || null;
        }

        if (type === "single") {
          const query = supabase.from("ad_performance").select("*", {
            count: "exact",
            head: true,
          });
          if (affiliateUUID) {
            query.eq("affiliate_uuid", affiliateUUID);
          }
          if (brand && brand !== "all") {
            query.eq("brand", brand);
          }
          // Get total count for the single date
          const { count, error: countError } = await query.eq("date", dates[0]);
          if (countError) throw countError;
          totalCount = count || 0;
          filter = (query) => query.eq("date", dates[0]);
        } else if (type === "range") {
          const query = supabase.from("ad_performance").select("*", {
            count: "exact",
            head: true,
          });
          if (affiliateUUID) {
            query.eq("affiliate_uuid", affiliateUUID);
          }
          if (brand && brand !== "all") {
            query.eq("brand", brand);
          }

          // Get total count for the date range
          const { count, error: countError } = await query
            .gte("date", dates[0])
            .lte("date", dates[1] + "T23:59:59");
          if (countError) throw countError;
          totalCount = count || 0;
          filter = (query) => query.gte("date", dates[0]).lte("date", dates[1]);
        } else {
          return [];
        }

        if (totalCount === 0) return [];

        const batchSize = 1000;
        const maxBatches = 100;
        const numBatches = Math.min(
          Math.ceil(totalCount / batchSize),
          maxBatches
        );

        const promises = [];
        for (let i = 0; i < numBatches; i++) {
          const from = i * batchSize;
          const to = Math.min(from + batchSize - 1, totalCount - 1);

          let query = supabase
            .from("ad_performance")
            .select("*")
            .range(from, to);

          if (affiliateUUID) {
            query.eq("affiliate_uuid", affiliateUUID);
          }
          if (brand && brand !== "all") {
            query.eq("brand", brand);
          }

          query = filter(query);

          promises.push(
            query.then(({ data, error }) => {
              if (error) throw error;
              return data;
            })
          );
        }

        const results = await Promise.all(promises);
        const allData = results.flat();

        const csvData = allData.map((row) => ({
          Date: row.date || "",
          "Ad Name": row.ad_name || "",
          "Campaign Name": row.campaign_name || "",
          "Adset Name": row.adset_name || "",
          "Ad Spend": row.spend || 0,
          "Pixel CV": row.revenue || 0,
          ROAS: row.spend > 0 ? row.revenue / row.spend : 0, // Calculate ROAS
          "Pixel ROAS": row.spend > 0 ? row.revenue / row.spend : 0, // Same as ROAS
          "Pixel Purchases": row.purchases || 0,
          "Pixel CPA": row.cpa || "",
          "Pixel AOV": row.aov || "",
          Profit: row.profit || 0,
          Commission: row.commission || 0,
          Clicks: row.clicks || 0,
          Impressions: row.impressions || 0,
          // Add affiliate extraction logic
          affiliate: row.affiliate_name || "",
        }));

        const { affiliates } = processAffiliateData(csvData);
        return affiliates;
      } catch (err) {
        console.error("Import error:", err);
        setError(err.message);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const pullAdPerformanceForAffiliate = useCallback(async (affiliateName) => {
    try {
      setIsLoading(true);
      setError(null);

      // PERFORMANCE: Check cache first
      const cacheKey = `affiliate_ads_${affiliateName}`;
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setSuccess(
          `Loaded ${cachedData.length} cached ad records for ${affiliateName}`
        );
        return cachedData;
      }

      // OPTIMIZED: Use RPC function that reads from finalized_daily_ad_earnings
      // This ensures commission values match payout center
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "get_individual_affiliate_ads_fast",
        {
          p_affiliate_name: affiliateName,
          brand_filter: null,
          start_date: null,
          end_date: null,
          limit_count: 50000,
        }
      );

      if (rpcError) {
        console.log("RPC function error, using fallback to finalized table");

        // Fallback: Query finalized_daily_ad_earnings directly
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("finalized_daily_ad_earnings")
          .select("*")
          .eq("affiliate_name", affiliateName)
          .gt("spend", 0)
          .order("spend", { ascending: false })
          .limit(50000);

        if (fallbackError) throw fallbackError;

        const processedData = processAdPerformanceData(
          fallbackData || [],
          affiliateName
        );
        setCachedData(cacheKey, processedData);
        setSuccess(
          `Loaded ${processedData.length} ad records for ${affiliateName} (fallback)`
        );
        return processedData;
      }

      // Process data from RPC function (commission is pre-calculated)
      const processedData = processAdPerformanceData(
        rpcData || [],
        affiliateName
      );
      setCachedData(cacheKey, processedData);
      setSuccess(
        `Loaded ${processedData.length} ad records for ${affiliateName} (finalized)`
      );
      return processedData;
    } catch (err) {
      console.error("Pull ad performance data for affiliate error:", err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [getCachedData]);

  // Helper function to process ad performance data
  // Now uses pre-calculated commission from finalized_daily_ad_earnings
  const processAdPerformanceData = (data, affiliateName) => {
    return data
      .filter((row) => {
        // Double-check that the ad belongs to this affiliate
        const extractedAffiliate = extractAffiliateName(row.ad_name || "");
        return extractedAffiliate === affiliateName;
      })
      .map((row) => {
        const spend = row.spend || 0;
        const revenue = row.revenue || 0;
        const roas = spend > 0 ? revenue / spend : 0;
        // Use pre-calculated commission from finalized table
        const commission = row.commission || 0;

        // Derive commission label from actual rate
        const commissionRate = spend > 0 ? commission / spend : 0;
        const commissionLabel = commissionRate >= 0.08 ? "8.0%" :
                               commissionRate >= 0.07 ? "7.0%" :
                               commissionRate >= 0.066 ? "6.6%" :
                               commissionRate >= 0.065 ? "6.5%" :
                               commissionRate >= 0.06 ? "6.0%" :
                               commissionRate >= 0.055 ? "5.5%" :
                               commissionRate > 0 ? `${(commissionRate * 100).toFixed(1)}%` : "0%";

        return {
          id: row.id,
          adName: row.ad_name || "",
          campaignName: row.campaign_name || "",
          adsetName: row.adset_name || "",
          date: row.date || row.ad_date, // Handle both field names
          adSpend: spend,
          revenue: revenue,
          roas: roas,
          profit: revenue - spend,
          commission: commission, // Pre-calculated from finalized table
          purchases: row.purchases || 0,
          clicks: row.clicks || 0,
          impressions: row.impressions || 0,
          cpa: row.cpa,
          aov: row.aov,
          commissionTier: null, // No longer recalculating tiers
          commissionLabel: commissionLabel,
          brand: row.brand || "",
        };
      });
  };

  // Function to recalculate all commission values in database
  const recalculateAllCommissions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc("recalculate_all_commissions");

      if (error) throw error;

      if (data && data.length > 0) {
        const result = data[0];
        setSuccess(
          `✅ Commission recalculation completed!\n` +
            `• Updated ${result.updated_count} records\n` +
            `• Old total: $${result.total_old_commission.toFixed(2)}\n` +
            `• New total: $${result.total_new_commission.toFixed(2)}\n` +
            `• Difference: $${(result.total_new_commission - result.total_old_commission).toFixed(2)}`
        );
        return result;
      }

      setSuccess("Commission recalculation completed successfully");
      return null;
    } catch (err) {
      console.error("Error recalculating commissions:", err);
      setError(`Failed to recalculate commissions: ${err.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // // Function to recalculate commission for specific affiliate
  // const recalculateAffiliateCommission = useCallback(async (affiliateName) => {
  //   try {
  //     setIsLoading(true);
  //     setError(null);

  //     const { data, error } = await supabase.rpc(
  //       "recalculate_affiliate_commission",
  //       { affiliate_name_param: affiliateName }
  //     );

  //     if (error) throw error;

  //     if (data && data.length > 0) {
  //       const result = data[0];
  //       setSuccess(
  //         `✅ Commission recalculation completed for ${affiliateName}!\n` +
  //           `• Updated ${result.updated_count} records\n` +
  //           `• Old total: $${result.total_old_commission.toFixed(2)}\n` +
  //           `• New total: $${result.total_new_commission.toFixed(2)}\n` +
  //           `• Difference: $${(result.total_new_commission - result.total_old_commission).toFixed(2)}`
  //       );
  //       return result;
  //     }

  //     setSuccess(`Commission recalculation completed for ${affiliateName}`);
  //     return null;
  //   } catch (err) {
  //     console.error("Error recalculating affiliate commission:", err);
  //     setError(
  //       `Failed to recalculate commission for ${affiliateName}: ${err.message}`
  //     );
  //     return null;
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, []);

  return {
    isLoading,
    error,
    success,
    debugInfo,
    importProgress,
    importMessage,
    clearMessages,
    loadData,
    getAvailableDates,
    getDateRangeSummary,
    fetchAllDataWithPagination,
    pullSummarizedData,
    importCSVDataInNewDashboard,
    importAdPerformanceDataByDates,
    pullAdPerformanceForAffiliate,
    recalculateAllCommissions,
    pullAllAffiliateData,
    clearCache,
  };
};

export default useSupabaseData;
